import React, { useEffect, useState } from "react";
import { Pencil, Power, Trash2 } from "lucide-react";
import API_BASE from "./apiBase";

const ManagerHelpManagement = ({ tenantId, token, onBack }) => {
  const [dpoContact, setDpoContact] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDpoForm, setShowDpoForm] = useState(false);

  // DPO Form State
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [responseTime, setResponseTime] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [dpoRes, qRes] = await Promise.all([
        fetch(`${API_BASE}/manager/help/dpo-contact?tenant_id=${encodeURIComponent(tenantId || '')}`, { headers }),
        fetch(`${API_BASE}/manager/help/contacts?tenant_id=${encodeURIComponent(tenantId || '')}`, { headers })
      ]);

      const dpoData = await dpoRes.json();
      const contactsData = await qRes.json();

      if (dpoData && Object.keys(dpoData).length) {
        setDpoContact(dpoData);
        setFullName(dpoData.fullName || '');
        setEmail(dpoData.email || '');
        setPhone(dpoData.phone || '');
        setAddress(dpoData.address || '');
        setResponseTime(dpoData.responseTime || '');
      }

      setContacts(Array.isArray(contactsData) ? contactsData : []);
    } catch (err) {
      console.error("❌ Failed to load data", err);
      setQuestions([]);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDpo = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setUpdateSuccess(null);

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const payload = {
        fullName,
        email,
        phone,
        address,
        responseTime
      };

      let res;
      if (dpoContact && dpoContact.id) {
        res = await fetch(`${API_BASE}/manager/help/dpo-contact/${dpoContact.id}?tenant_id=${encodeURIComponent(tenantId || '')}`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, tenant_id: tenantId }),
        });
      } else {
        res = await fetch(`${API_BASE}/manager/help/dpo-contact`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ ...payload, tenant_id: tenantId })
        });
      }

      const data = await res.json();
      if (res.ok && data.success) {
        setUpdateSuccess(true);
        setShowDpoForm(false);
        await loadData();
        setTimeout(() => setUpdateSuccess(null), 2200);
      } else {
        setUpdateSuccess(false);
      }
    } catch (err) {
      console.error('Failed to update DPO contact', err);
      setUpdateSuccess(false);
    } finally {
      setUpdating(false);
    }
  };

  const resetDpoForm = () => {
    setDpoContact(null);
    setFullName('');
    setEmail('');
    setPhone('');
    setAddress('');
    setResponseTime('');
    setUpdateSuccess(null);
  };

  const canCreateNewDpo = contacts.length === 0 || contacts.every(contact => contact.active === false);

  const openDpoForm = (contact = null) => {
    if (contact) {
      setDpoContact(contact);
      setFullName(contact.fullName || '');
      setEmail(contact.email || '');
      setPhone(contact.phone || '');
      setAddress(contact.address || '');
      setResponseTime(contact.responseTime || '');
    } else {
      if (!canCreateNewDpo) {
        window.alert('A DPO record already exists and is active. Please deactivate the current DPO before adding a new one.');
        return;
      }
      resetDpoForm();
    }
    setShowDpoForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeDpoForm = () => {
    setShowDpoForm(false);
    setUpdateSuccess(null);
  };

  const editContact = (contact) => {
    openDpoForm(contact);
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Delete this DPO form record?')) return;
    const res = await fetch(`${API_BASE}/manager/help/dpo-contact/${contactId}?tenant_id=${encodeURIComponent(tenantId || '')}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to delete DPO contact');
    await loadData();
  };

  const toggleContactActive = async (contact) => {
    const res = await fetch(`${API_BASE}/manager/help/dpo-contact/${contact.id}?tenant_id=${encodeURIComponent(tenantId || '')}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: contact.active === false }),
    });
    if (!res.ok) throw new Error('Failed to update DPO contact status');
    await loadData();
  };

  if (loading) {
    return (
      <div className="manager-help-container">
        <div className="simple-loading"></div>
      </div>
    );
  }

  return (
    <div className="manager-help-container">
      {/* Icon-only action buttons + tooltip styles */}
      <style>{`
        .action-icons-group {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .icon-action-btn {
          position: relative;
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          cursor: pointer;
          transition: all 0.15s ease;
          padding: 0;
        }

        .icon-action-btn svg {
          display: block !important;
          width: 16px !important;
          height: 16px !important;
          stroke: currentColor !important;
          flex-shrink: 0;
          pointer-events: none;
        }

        .icon-action-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
        }

        .icon-action-edit {
          color: #2563eb;
          border-color: #bfdbfe;
        }
        .icon-action-edit:hover {
          background: #eff6ff;
        }

        .icon-action-activate {
          color: #16a34a;
          border-color: #bbf7d0;
        }
        .icon-action-activate:hover {
          background: #f0fdf4;
        }

        .icon-action-deactivate {
          color: #d97706;
          border-color: #fde68a;
        }
        .icon-action-deactivate:hover {
          background: #fffbeb;
        }

        .icon-action-delete {
          color: #dc2626;
          border-color: #fecaca;
        }
        .icon-action-delete:hover {
          background: #fef2f2;
        }

        .icon-tooltip {
          position: absolute;
          bottom: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%) translateY(4px);
          background: #1e293b;
          color: #fff;
          font-size: 12px;
          font-weight: 500;
          padding: 5px 10px;
          border-radius: 6px;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.15s ease, transform 0.15s ease;
          z-index: 20;
        }

        .icon-tooltip::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: #1e293b;
        }

        .icon-action-btn:hover .icon-tooltip {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
      `}</style>

      {/* Header */}
      <div className="manager-help-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1>Help & Support Management</h1>
          <p>Manage DPO information</p>
        </div>
        <button
          type="button"
          onClick={() => typeof onBack === 'function' ? onBack() : window.history.back()}
          style={{
            padding: '10px 16px',
            border: '1px solid #cbd5e1',
            borderRadius: 8,
            background: '#ffffff',
            color: '#1f2937',
            cursor: 'pointer',
            fontWeight: 600,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
          }}
        >
          ← Back
        </button>
      </div>

      <div className="manager-help-grid">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div />
          <button
            type="button"
            onClick={() => openDpoForm()}
            disabled={!canCreateNewDpo}
            title={!canCreateNewDpo ? 'Deactivate the active DPO before adding a new one.' : 'Add a new DPO form'}
            style={{
              padding: '10px 16px',
              border: '1px solid #cbd5e1',
              borderRadius: 8,
              background: canCreateNewDpo ? '#ffffff' : '#f1f5f9',
              color: canCreateNewDpo ? '#1f2937' : '#94a3b8',
              cursor: canCreateNewDpo ? 'pointer' : 'not-allowed',
              fontWeight: 600,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              opacity: canCreateNewDpo ? 1 : 0.75,
            }}
          >
            + Add DPO Form
          </button>
        </div>

        {showDpoForm && (
          <div className="manager-section dpo-section-card">
            <div className="section-header dpo-section-header">
              <div className="dpo-header-title-wrap">
                <span className="dpo-header-accent-bar" />
                <div>
                  <h2>{dpoContact ? 'Edit DPO Contact Information' : 'DPO Contact Information'}</h2>
                  <p>This information will be displayed to users in the DSAR Help & Support section for assistance and guidance.</p>
                </div>
              </div>
            </div>

            <form className="dpo-form-container" onSubmit={handleUpdateDpo}>
              <div className="dpo-form-grid">
                <div className="dpo-field-group">
                  <label htmlFor="fullName">
                    Full Name <span className="required-star">*</span>
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    className="dpo-input"
                    placeholder="Enter full name"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    required
                  />
                </div>
                <div className="dpo-field-group">
                  <label htmlFor="email">
                    Email <span className="required-star">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    className="dpo-input"
                    placeholder="Enter email address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="dpo-field-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    id="phone"
                    type="tel"
                    className="dpo-input"
                    placeholder="Enter phone number"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                  />
                </div>

                <div className="dpo-field-group">
                  <label htmlFor="responseTime">Response Time</label>
                  <input
                    id="responseTime"
                    type="text"
                    className="dpo-input"
                    placeholder="e.g., Within 24-48 hours"
                    value={responseTime}
                    onChange={e => setResponseTime(e.target.value)}
                  />
                </div>
                <div className="dpo-field-group dpo-field-full">
                  <label htmlFor="address">Address</label>
                  <textarea
                    id="address"
                    className="dpo-input dpo-textarea"
                    placeholder="Enter address"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>

              <div className="dpo-form-actions" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button type="submit" className="dpo-submit-btn" disabled={updating}>
                  {updating ? (
                    <>
                      <span className="spinner"></span>
                      Updating...
                    </>
                  ) : (
                    <>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {dpoContact ? 'Update Information' : 'Create Information'}
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={closeDpoForm}
                  style={{
                    padding: '10px 16px',
                    border: '1px solid #cbd5e1',
                    borderRadius: 8,
                    background: '#ffffff',
                    color: '#1f2937',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Cancel
                </button>
              </div>

              {updateSuccess === true && (
                <div className="alert alert-success dpo-alert">
                  ✓ DPO information updated successfully!
                </div>
              )}
              {updateSuccess === false && (
                <div className="alert alert-error dpo-alert">
                  ✕ Failed to update DPO information. Please try again.
                </div>
              )}
            </form>
          </div>
        )}
      </div>

      <div className="manager-section" style={{ marginTop: 24, overflowX: 'auto' }}>
        <div className="section-header">
          <h2>DPO Forms Submitted for This Tenant</h2>
          <p>{contacts.length} form{contacts.length === 1 ? '' : 's'} found</p>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead>
            <tr>
              {['Name', 'Email', 'Phone', 'Address', 'Response Time', 'Status', 'Actions'].map((heading) => (
                <th key={heading} style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact) => (
              <tr key={contact.id}>
                <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>{contact.fullName || '—'}</td>
                <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>{contact.email || '—'}</td>
                <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>{contact.phone || '—'}</td>
                <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>{contact.address || '—'}</td>
                <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>{contact.responseTime || '—'}</td>
                <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>{contact.active === false ? 'Inactive' : 'Active'}</td>
                <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>
                  <div className="action-icons-group">
                    <button
                      type="button"
                      className="icon-action-btn icon-action-edit"
                      onClick={() => editContact(contact)}
                    >
                      <Pencil size={16} color="#2563eb" strokeWidth={2} />
                      <span className="icon-tooltip">Edit DPO form</span>
                    </button>

                    <button
                      type="button"
                      className={`icon-action-btn ${contact.active === false ? 'icon-action-activate' : 'icon-action-deactivate'}`}
                      onClick={() => toggleContactActive(contact)}
                    >
                      <Power
                        size={16}
                        color={contact.active === false ? '#16a34a' : '#d97706'}
                        strokeWidth={2}
                      />
                      <span className="icon-tooltip">
                        {contact.active === false ? 'Activate' : 'Make Inactive'}
                      </span>
                    </button>

                    <button
                      type="button"
                      className="icon-action-btn icon-action-delete"
                      onClick={() => deleteContact(contact.id)}
                    >
                      <Trash2 size={16} color="#dc2626" strokeWidth={2} />
                      <span className="icon-tooltip">Delete DPO form</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!contacts.length && (
              <tr>
                <td colSpan={7} style={{ padding: 20, textAlign: 'center' }}>
                  No DPO forms found for this tenant.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManagerHelpManagement;